import { Injectable, signal } from '@angular/core';

/** Route path both login-step components redirect to for an enforced password change. */
export const RESET_PASSWORD_ROUTE = 'reset-password';

/**
 * In-memory handoff for the enforced password-change journey,
 * state lives only in memory, never in the URL, and is reset once consumed.
 */
@Injectable({ providedIn: 'root' })
export class PasswordChangeStateService {
  private readonly changePasswordTokenSignal = signal<string | null>(null);
  private readonly emailAddressSignal = signal<string | null>(null);
  private readonly successPendingSignal = signal(false);
  private readonly tokenErrorPendingSignal = signal(false);

  readonly changePasswordToken = this.changePasswordTokenSignal.asReadonly();
  readonly emailAddress = this.emailAddressSignal.asReadonly();

  set(changePasswordToken: string, emailAddress: string): void {
    this.changePasswordTokenSignal.set(changePasswordToken);
    this.emailAddressSignal.set(emailAddress);
    this.successPendingSignal.set(false);
    this.tokenErrorPendingSignal.set(false);
  }

  /**
   * Hands off the token and email when a login-step is about to redirect to the
   * reset-password route, shared by the login form and 2-step verification's
   * `PASSWORD_EXPIRED` handling so both stay in sync.
   */
  handOffForRoute(
    route: string,
    changePasswordToken: string | null | undefined,
    emailAddress: string
  ): void {
    if (route === RESET_PASSWORD_ROUTE && changePasswordToken) {
      this.set(changePasswordToken, emailAddress);
    }
  }

  reset(): void {
    this.changePasswordTokenSignal.set(null);
    this.emailAddressSignal.set(null);
    this.successPendingSignal.set(false);
    this.tokenErrorPendingSignal.set(false);
  }

  /**
   * Marks a completed password change so the login form shows a success alert once.
   * Clears the token but keeps emailAddress for login prefill via consumeEmail().
   * When emailAddress is passed, it is stored for that prefill handoff.
   */
  markSuccess(emailAddress?: string): void {
    this.changePasswordTokenSignal.set(null);
    if (emailAddress) {
      this.emailAddressSignal.set(emailAddress);
    }
    this.successPendingSignal.set(true);
    this.tokenErrorPendingSignal.set(false);
  }

  /** Returns and clears the one-shot email for login prefill after a successful password change. */
  consumeEmail(): string | null {
    const emailAddress = this.emailAddressSignal();
    this.emailAddressSignal.set(null);
    return emailAddress;
  }

  /** Reads and clears the one-shot success flag; a second read returns false. */
  consumeSuccess(): boolean {
    const wasPending = this.successPendingSignal();
    this.successPendingSignal.set(false);
    return wasPending;
  }

  /**
   * Marks a lost, invalid, or expired change-password token so the login form
   * shows a recoverable alert once, instead of silently returning to a blank
   * login page.
   */
  markTokenError(): void {
    this.changePasswordTokenSignal.set(null);
    this.emailAddressSignal.set(null);
    this.tokenErrorPendingSignal.set(true);
    this.successPendingSignal.set(false);
  }

  /** Reads and clears the one-shot token-error flag; a second read returns false. */
  consumeTokenError(): boolean {
    const wasPending = this.tokenErrorPendingSignal();
    this.tokenErrorPendingSignal.set(false);
    return wasPending;
  }
}
